import {
  CognitoUser,
  CognitoUserPool,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js'
import cnf from '@/config'

export default class CognitoAuth {
  private static instance: CognitoAuth
  private userPool: CognitoUserPool | null = null
  private options: CognitoConfig | null = null
  private userSession: CognitoUserSession | null = null
  private user: CognitoUser | null = null
  private constructor() {}

  static getInstance(): CognitoAuth {
    if (!CognitoAuth.instance) {
      CognitoAuth.instance = new CognitoAuth()
      CognitoAuth.instance.configure(cnf)
      // load user from local storage
      CognitoAuth.instance.loadUserSession()
    }
    return CognitoAuth.instance
  }

  private readonly loadUserSession: () => void = () => {
    if (this.userPool == null) {
      return
    }
    const user = CognitoAuth.instance.getCurrentUser()
    user?.getSession((err: any, session: any) => {
      if (err) {
        return
      }
      if (session.isValid()) {
        this.userSession = session
        this.user = user
      }
    })
    this.user = user ?? null
  }

  getCurrentUser(): CognitoUser | null | undefined {
    return this.userPool?.getCurrentUser()
  }

  setUser(user: CognitoUser) {
    this.user = user
  }

  async isAuthenticated(): Promise<boolean> {
    return await new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser()
      if (cognitoUser != null) {
        cognitoUser.getSession((err: any) => {
          if (err) {
            reject(err)
          }
          resolve(true)
        })
      } else {
        resolve(false)
      }
    })
  }

  configure(config: CognitoConfig) {
    if (typeof config !== 'object' || Array.isArray(config)) {
      throw new Error('[CognitoAuth error] valid option object required')
    }
    this.userPool = new CognitoUserPool({
      UserPoolId: config.IdentityPoolId,
      ClientId: config.ClientId
    })
    this.options = config
  }

  async login(dataUser: any): Promise<CognitoUserSession> {
    const userSession: CognitoUserSession = await new Promise(
      (resolve, reject) => {
        const authenticationData = {
          Username: dataUser.userEmail,
          Password: dataUser.userPassword
        }
        const authenticationDetails = new AuthenticationDetails(
          authenticationData
        )
        if (this.userPool == null) {
          reject(new Error('User pool not configured'))
          return
        }

        const userData = { Username: dataUser.userEmail, Pool: this.userPool }
        const cognitoUser = new CognitoUser(userData)
        this.user = cognitoUser
        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: function (result: CognitoUserSession) {
            resolve(result)
          },
          onFailure: function (err) {
            reject(err)
          },
          newPasswordRequired: function (userAttributes, requiredAttributes) {
            if (dataUser.userNewPassword === '') {
              const e = new Error('temporary password change is requested')
              e.name = 'ForceChangePassword'
              reject(e)
            } else {
              cognitoUser.completeNewPasswordChallenge(
                dataUser.userNewPassword,
                requiredAttributes,
                {
                  onSuccess: function (result: CognitoUserSession) {
                    resolve(result)
                  },
                  onFailure: function (err) {
                    reject(err)
                  }
                }
              )
            }
          },
          mfaRequired: function (codeDeliveryDetails) {
            reject(new MFARequiredException(codeDeliveryDetails))
          }
        })
      }
    )
    this.userSession = userSession
    return userSession
  }

  async changePassword(pass: string, newPass: string): Promise<boolean> {
    return await new Promise((resolve, reject) => {
      if (this.userPool == null) {
        reject(new Error('User pool not configured'))
        return
      }

      this.user?.changePassword(pass, newPass, function (err, result) {
        if (err != null) {
          reject(err)
          return
        }
        resolve(true)
      })
    })
  }

  async changeNameAndPassword(
    newName: string,
    oldPass: string,
    newPass: string
  ): Promise<boolean> {
    const changeName: boolean = await new Promise((resolve, reject) => {
      if (!this.user) {
        reject(new Error('User not logged in'))
        return
      }
      const attributeList = []
      attributeList.push(
        new CognitoUserAttribute({
          Name: 'name',
          Value: newName
        })
      )

      this.user?.updateAttributes(attributeList, function (err, result) {
        if (err != null) {
          reject(err)
          return
        }
        resolve(true)
      })
    })

    return changeName && (await this.changePassword(oldPass, newPass))
  }

  async passwordReset(username: string): Promise<boolean | string> {
    return await new Promise((resolve, reject) => {
      if (this.userPool == null) {
        reject(new Error('User pool not configured'))
        return
      }
      const userData = { Username: username, Pool: this.userPool }
      const cognitoUser = new CognitoUser(userData)
      cognitoUser.forgotPassword({
        onSuccess: function (result) {
          resolve(true)
        },
        onFailure: function (err) {
          resolve(err.message)
        },
        inputVerificationCode() {
          resolve(true)
        }
      })
    })
  }

  async confirmPasswordReset(
    username: string,
    verificationCode: string,
    newPassword: string
  ): Promise<boolean> {
    return await new Promise((resolve, reject) => {
      if (this.userPool == null) {
        reject(new Error('User pool not configured'))
        return
      }
      const userData = { Username: username, Pool: this.userPool }
      const cognitoUser = new CognitoUser(userData)
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: function (result) {
          resolve(true)
        },
        onFailure: function (err) {
          reject(err)
        }
      })
    })
  }

  logout() {
    this.getCurrentUser()?.signOut()
  }

  async getAttributes(cognitoUser: CognitoUser): Promise<any> {
    return await new Promise((resolve, reject) => {
      cognitoUser.getSession((err: any) => {
        if (err) {
          reject(err)
        }

        cognitoUser.getUserAttributes((err, result) => {
          if (err != null) {
            reject(err)
            return
          }
          resolve(
            result?.reduce((acc: any, item) => {
              acc[item.getName().replace('custom:', '')] = item.getValue()
              return acc
            }, {})
          )
        })
      })
    })
  }

  getIdToken(): {
    token: string
    expiresAt: number
  } {
    return {
      token: this.userSession?.getIdToken().getJwtToken() ?? '',
      expiresAt: this.userSession?.getIdToken().getExpiration() ?? 0
    }
  }

  async getApsClaims(token: string): Promise<ApsClaims> {
    const unParsedClaims = this.parseJwt(token)

    const claims = JSON.parse(
      unParsedClaims?.['https://nominasaps.com/jwt/claims']
    )

    const cognitoGroups = Array.isArray(unParsedClaims?.['cognito:groups'])
      ? unParsedClaims['cognito:groups']
      : []

    const isApsAdmin = cognitoGroups.includes('APS_ADMIN')

    return {
      xUserId: claims?.['x-user-id'] ?? '',
      xUserName: claims?.['x-user-name'] ?? '',
      xUserEmail: unParsedClaims?.email ?? '',
      xDefaultRole: claims?.['x-default-role'] ?? '',
      xAllowedRoles: claims?.['x-allowed-roles'] ?? [],
      xWorkspaceId: claims?.['x-workspace-id'] ?? '',
      cognitoGroups,
      isApsAdmin
    }
  }

  parseJwt(token: string) {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        })
        .join('')
    )

    return JSON.parse(jsonPayload)
  }

  refreshToken(): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      if (!this.user) {
        reject(new Error('User not initialized'))
        return
      }
      this.user.getSession((err: any, session: any) => {
        if (err) {
          reject(err)
          return
        }
        if (session.isValid()) {
          this.userSession = session
          resolve(session)
        } else {
          const refreshToken = this.userSession?.getRefreshToken()
          if (!refreshToken) {
            reject(new Error('Refresh token not found'))
            return
          }
          this.user?.refreshSession(refreshToken, (err, refreshedSession) => {
            if (err) {
              reject(err)
              return
            }
            this.userSession = refreshedSession
            resolve(refreshedSession)
          })
        }
      })
    })
  }
}

interface ApsClaims {
  xUserId: string
  xUserName: string
  xUserEmail: string
  xDefaultRole: string
  xAllowedRoles: string[]
  xWorkspaceId: string
  cognitoGroups?: string[]
  isApsAdmin?: boolean
}

interface CognitoConfig {
  region: string
  IdentityPoolId: string
  UserPoolId: string
  ClientId: string
}

class MFARequiredException extends Error {
  constructor(public codeDeliveryDetails: any) {
    super('MFA Required')
    this.name = 'MFARequiredException'
    this.codeDeliveryDetails = codeDeliveryDetails
  }
}
